/**
 * AuthContext - Phase 4.3 of the CJM Overhaul
 *
 * Single source of truth for authentication state. Replaces scattered
 * `localStorage.getItem('adminToken')` / `sessionStorage.getItem('workerToken')`
 * calls across ~200 component files with a single React context.
 *
 * Backward-compatible: wraps utils/storage.js so existing code that still
 * reads tokens directly continues to work. New code should use useAuth().
 *
 * Roles supported:
 *   Admin    - Superadmin (platform owner)
 *   Company  - Company owner / admin user
 *   Worker   - Staff member / employee
 *   Customer - End customer (client portal)
 *   Donor    - Donor (non-profit mode)
 *
 * Usage:
 *   import { useAuth } from '../contexts/AuthContext';
 *   const { user, token, role, isAuthenticated, login, logout } = useAuth();
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import storage, { getItem, setItem, removeItem, clearAuth } from "../utils/storage";

// Token priority order - the first non-empty one wins
const TOKEN_KEYS = ["adminToken", "workerToken", "customerToken", "donorToken"];

// Map token key -> role label
const TOKEN_ROLE_MAP = {
  adminToken:    "Admin",
  workerToken:   "Worker",
  customerToken: "Customer",
  donorToken:    "Donor",
};

const AuthContext = createContext(null);

function readActiveTokenFromStorage() {
  for (const key of TOKEN_KEYS) {
    const val = getItem(key);
    if (val) return { token: val, tokenKey: key };
  }
  return { token: null, tokenKey: null };
}

function safeDecode(token) {
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch (_) {
    return null;
  }
}

function isExpired(decoded) {
  if (!decoded || !decoded.exp) return false;
  return decoded.exp * 1000 < Date.now();
}

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState(() => {
    const { token, tokenKey } = readActiveTokenFromStorage();
    const decoded = safeDecode(token);
    return {
      token,
      tokenKey,
      decoded,
      // Role comes from decoded token (set by authMiddleware) OR falls back
      // to the token-key mapping (useful when the token is missing claims)
      role: decoded?.Role || (tokenKey ? TOKEN_ROLE_MAP[tokenKey] : null),
    };
  });

  // Re-sync with storage when other tabs update auth (storage event only
  // fires cross-tab, but good to have for multi-tab logout)
  useEffect(() => {
    const handler = (e) => {
      if (e && e.key && !TOKEN_KEYS.includes(e.key)) return;
      const { token, tokenKey } = readActiveTokenFromStorage();
      const decoded = safeDecode(token);
      setAuthState({
        token,
        tokenKey,
        decoded,
        role: decoded?.Role || (tokenKey ? TOKEN_ROLE_MAP[tokenKey] : null),
      });
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const login = useCallback(({ token, role }) => {
    // Derive which storage key to use from the role (matches legacy behavior)
    const keyForRole = {
      Admin:    "adminToken",
      Company:  "adminToken",       // Company tokens are stored under adminToken (legacy)
      Worker:   "workerToken",
      Customer: "customerToken",
      Donor:    "donorToken",
    };
    const tokenKey = keyForRole[role] || "adminToken";
    setItem(tokenKey, token);
    const decoded = safeDecode(token);
    setAuthState({
      token,
      tokenKey,
      decoded,
      role: decoded?.Role || role || TOKEN_ROLE_MAP[tokenKey],
    });
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    setAuthState({ token: null, tokenKey: null, decoded: null, role: null });
  }, []);

  // Convenience: refresh the context from storage (call after a token refresh
  // performed by the axios interceptor)
  const syncFromStorage = useCallback(() => {
    const { token, tokenKey } = readActiveTokenFromStorage();
    const decoded = safeDecode(token);
    setAuthState({
      token,
      tokenKey,
      decoded,
      role: decoded?.Role || (tokenKey ? TOKEN_ROLE_MAP[tokenKey] : null),
    });
  }, []);

  const value = useMemo(() => {
    const { token, tokenKey, decoded, role } = authState;
    const expired = isExpired(decoded);
    return {
      token,
      tokenKey,
      decoded,
      role,
      user: decoded
        ? {
            UserId: decoded.UserId,
            Role: decoded.Role,
            CompanyId: decoded.CompanyId,
            EmailAddress: decoded.EmailAddress,
            OwnerName: decoded.OwnerName,
            IsDonationMode: decoded.IsDonationMode,
            AdminId: decoded.AdminId,
            Timezone: decoded.Timezone,
          }
        : null,
      isAuthenticated: Boolean(token) && !expired,
      isExpired: expired,
      login,
      logout,
      syncFromStorage,
      // Raw storage helpers re-exported for transitional code
      storage,
      getItem,
      setItem,
      removeItem,
    };
  }, [authState, login, logout, syncFromStorage]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    // Render outside provider: return a safe, non-authenticated stub so
    // components that haven't been migrated to the provider still function.
    return {
      token: null,
      tokenKey: null,
      decoded: null,
      role: null,
      user: null,
      isAuthenticated: false,
      isExpired: false,
      login: () => {},
      logout: () => {},
      syncFromStorage: () => {},
      storage,
      getItem,
      setItem,
      removeItem,
    };
  }
  return ctx;
}

export default AuthContext;
